import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Stack,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Event as EventIcon } from '@mui/icons-material';
import { EventItem, EventDate, SetEventDateOccurrence, WeekdayCode, RecurringByWeekdayNth } from 'types/events.types';
import { Website } from 'types/website.types';
import LocationSelector from 'features/locations/LocationSelector';
import CollapsibleSection from 'components/common/CollapsibleSection';

export interface EventFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<EventItem>) => Promise<void> | void;
  isEdit?: boolean;
  event?: EventItem;
  websites: Website[];
}

const weekdayLabels: { code: WeekdayCode; label: string }[] = [
  { code: 'SU', label: 'Sun' },
  { code: 'MO', label: 'Mon' },
  { code: 'TU', label: 'Tue' },
  { code: 'WE', label: 'Wed' },
  { code: 'TH', label: 'Thu' },
  { code: 'FR', label: 'Fri' },
  { code: 'SA', label: 'Sat' },
];

const fmtDate = (d?: string) => {
  if (!d) return '';
  const dt = new Date(d);
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(dt);
};
const fmtDateWithYear = (d?: string) => {
  if (!d) return '';
  const dt = new Date(d);
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(dt);
};
const fmtAmpm = (t?: string) => {
  if (!t) return '';
  const [hStr, mStr = '00'] = t.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10) || 0;
  const ampm = h < 12 ? 'a.m.' : 'p.m.';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}${m ? `:${String(m).padStart(2, '0')}` : ''} ${ampm}`;
};
const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Helper: parse month name to number
const monthToNumber = (m: string): number | null => {
  const s = m.trim().replace(/\.$/, '').toLowerCase();
  const map: Record<string, number> = {
    jan: 1, january: 1,
    feb: 2, february: 2,
    mar: 3, march: 3,
    apr: 4, april: 4,
    may: 5,
    jun: 6, june: 6,
    jul: 7, july: 7,
    aug: 8, august: 8,
    sep: 9, sept: 9, september: 9,
    oct: 10, october: 10,
    nov: 11, november: 11,
    dec: 12, december: 12,
  };
  return map[s] || null;
};

const weekdayToCode = (w: string): WeekdayCode | null => {
  const s = w.trim().toLowerCase();
  const map: Record<string, WeekdayCode> = {
    sunday: 'SU', sundays: 'SU', sun: 'SU',
    monday: 'MO', mondays: 'MO', mon: 'MO',
    tuesday: 'TU', tuesdays: 'TU', tue: 'TU', tues: 'TU',
    wednesday: 'WE', wednesdays: 'WE', wed: 'WE',
    thursday: 'TH', thursdays: 'TH', thu: 'TH', thurs: 'TH',
    friday: 'FR', fridays: 'FR', fri: 'FR',
    saturday: 'SA', saturdays: 'SA', sat: 'SA',
  };
  return map[s] || null;
};

const parseTimeString = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  const s = raw.trim().toLowerCase();
  if (!s) return undefined;
  if (s.includes('noon')) return '12:00';
  if (s.includes('midnight')) return '00:00';
  const match = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?|am|pm)?$/i);
  if (!match) return undefined;
  let h = parseInt(match[1], 10);
  const m = match[2] ? parseInt(match[2], 10) : 0;
  const ampm = match[3]?.replace(/\./g, '').toLowerCase();
  if (ampm === 'pm' && h < 12) h += 12;
  if (ampm === 'am' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const EventForm: React.FC<EventFormProps> = ({ open, onClose, onSubmit, isEdit = false, event, websites }) => {
  const [title, setTitle] = useState('');
  const [websitePK, setWebsitePK] = useState('');
  const [venue, setVenue] = useState<{ name: string; rating?: number; reviews?: number }>({ name: '' });
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState<string[]>(['']);
  const [link, setLink] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [locationPK, setLocationPK] = useState('');

  const [dateKind, setDateKind] = useState<'legacy' | 'single' | 'span' | 'set' | 'recurring'>('single');
  const [timezone, setTimezone] = useState<string>('');

  const [singleStart, setSingleStart] = useState<string>('');
  const [singleEnd, setSingleEnd] = useState<string>('');

  const [spanStartDate, setSpanStartDate] = useState<string>('');
  const [spanEndDate, setSpanEndDate] = useState<string>('');
  const [spanStartTime, setSpanStartTime] = useState<string>('');
  const [spanEndTime, setSpanEndTime] = useState<string>('');

  const [occurrences, setOccurrences] = useState<SetEventDateOccurrence[]>([{ date: '', startTime: '', endTime: '' }]);

  const [freq, setFreq] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'>('WEEKLY');
  const [dtstart, setDtstart] = useState<string>('');
  const [until, setUntil] = useState<string>('');
  const [byWeekdaySimple, setByWeekdaySimple] = useState<WeekdayCode[]>([]);
  const [byWeekdayNth, setByWeekdayNth] = useState<RecurringByWeekdayNth[]>([]);
  const [ruleStartTime, setRuleStartTime] = useState<string>('');
  const [ruleEndTime, setRuleEndTime] = useState<string>('');
  const [exclusions, setExclusions] = useState<string>('');
  const [inclusions, setInclusions] = useState<string>('');

  const [legacyStart, setLegacyStart] = useState<string>('');
  const [legacyWhen, setLegacyWhen] = useState<string>('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isEdit && event) {
      setTitle(event.title || '');
      setWebsitePK(event.websitePK || (websites[0]?.id ? (websites[0].id.startsWith('WEBSITE#') ? websites[0].id : `WEBSITE#${websites[0].id}`) : ''));
      setVenue(event.venue || { name: '' });
      setDescription(event.description || '');
      setAddress(event.address?.length ? [...event.address] : ['']);
      setLink(event.link || '');
      setThumbnail(event.thumbnail || '');
      setImageUrl(event.imageUrl || '');
      setLocationPK(event.locationPK || '');

      const d: any = event.date;
      if (d && d.kind) {
        setDateKind(d.kind);
        setTimezone(d.timezone || '');
        if (d.kind === 'single') {
          setSingleStart(d.start ? toLocalDatetimeInput(d.start) : '');
          setSingleEnd(d.end ? toLocalDatetimeInput(d.end) : '');
        } else if (d.kind === 'span') {
          setSpanStartDate(d.startDate || '');
          setSpanEndDate(d.endDate || '');
          setSpanStartTime(d.startTime || '');
          setSpanEndTime(d.endTime || '');
        } else if (d.kind === 'set') {
          setOccurrences(d.occurrences && d.occurrences.length ? d.occurrences : [{ date: '', startTime: '', endTime: '' }]);
        } else if (d.kind === 'recurring') {
          setFreq(d.rule?.freq || 'WEEKLY');
          setDtstart(d.rule?.dtstart || '');
          setUntil(d.rule?.until || '');
          const bw = d.rule?.byWeekday || [];
          const simple: WeekdayCode[] = [];
          const nth: RecurringByWeekdayNth[] = [];
          bw.forEach((w: any) => {
            if (typeof w === 'string') simple.push(w as WeekdayCode);
            else if (w && w.weekday) nth.push({ weekday: w.weekday as WeekdayCode, nth: Array.isArray(w.nth) ? w.nth : [] });
          });
          setByWeekdaySimple(simple);
          setByWeekdayNth(nth);
          setRuleStartTime(d.rule?.startTime || '');
          setRuleEndTime(d.rule?.endTime || '');
          setExclusions((d.rule?.exclusions || []).join(','));
          setInclusions((d.rule?.inclusions || []).join(','));
        }
      } else {
        setDateKind('legacy');
        setLegacyStart(d?.start_date ? toLocalDatetimeInput(d.start_date) : '');
        setLegacyWhen(d?.when || '');
      }
    } else {
      setTitle('');
      setWebsitePK(websites[0]?.id ? (websites[0].id.startsWith('WEBSITE#') ? websites[0].id : `WEBSITE#${websites[0].id}`) : '');
      setVenue({ name: '' });
      setDescription('');
      setAddress(['']);
      setLink('');
      setThumbnail('');
      setImageUrl('');
      setLocationPK('');
      setDateKind('single');
      setTimezone('');
      setSingleStart('');
      setSingleEnd('');
      setSpanStartDate('');
      setSpanEndDate('');
      setSpanStartTime('');
      setSpanEndTime('');
      setOccurrences([{ date: '', startTime: '', endTime: '' }]);
      setFreq('WEEKLY');
      setDtstart('');
      setUntil('');
      setByWeekdaySimple([]);
      setByWeekdayNth([]);
      setRuleStartTime('');
      setRuleEndTime('');
      setExclusions('');
      setInclusions('');
      setLegacyStart('');
      setLegacyWhen('');
    }
  }, [isEdit, event, websites]);

  const websiteOptions = useMemo(() => websites.map(w => ({
    value: w.id.startsWith('WEBSITE#') ? w.id : `WEBSITE#${w.id}`,
    label: w.name,
  })), [websites]);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!title.trim()) e['title'] = 'Title is required';
    if (!websitePK) e['websitePK'] = 'Website is required';
    if (!description.trim()) e['description'] = 'Description is required';
    if (!address || !address[0]?.trim()) e['address.0'] = 'Address line is required';

    if (dateKind === 'single') {
      if (!singleStart) e['date.singleStart'] = 'Start is required';
    } else if (dateKind === 'span') {
      if (!spanStartDate) e['date.spanStartDate'] = 'Start date is required';
      if (!spanEndDate) e['date.spanEndDate'] = 'End date is required';
    } else if (dateKind === 'set') {
      if (!occurrences.length || !occurrences[0].date) e['date.set.0'] = 'At least one date is required';
    } else if (dateKind === 'recurring') {
      if (!freq) e['date.recurring.freq'] = 'Frequency is required';
    } else if (dateKind === 'legacy') {
      if (!legacyStart) e['date.legacy.start'] = 'Start date/time is required';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildEventDate = (): EventDate => {
    if (dateKind === 'single') {
      return {
        kind: 'single',
        timezone: timezone || undefined,
        start: toIsoFromLocal(singleStart) || '',
        end: singleEnd ? toIsoFromLocal(singleEnd) : undefined,
      } as any;
    }
    if (dateKind === 'span') {
      return {
        kind: 'span',
        timezone: timezone || undefined,
        startDate: spanStartDate,
        endDate: spanEndDate,
        startTime: spanStartTime || undefined,
        endTime: spanEndTime || undefined,
      } as any;
    }
    if (dateKind === 'set') {
      return {
        kind: 'set',
        timezone: timezone || undefined,
        occurrences: occurrences.filter(o => o.date),
      } as any;
    }
    if (dateKind === 'recurring') {
      const byWeekday: (WeekdayCode | RecurringByWeekdayNth)[] = [
        ...byWeekdaySimple,
        ...byWeekdayNth.filter(x => x.weekday && x.nth && x.nth.length > 0),
      ];
      return {
        kind: 'recurring',
        timezone: timezone || undefined,
        rule: {
          freq,
          dtstart: dtstart || undefined,
          until: until || undefined,
          byWeekday: byWeekday.length ? byWeekday : undefined,
          startTime: ruleStartTime || undefined,
          endTime: ruleEndTime || undefined,
          exclusions: exclusions ? exclusions.split(',').map(s => s.trim()).filter(Boolean) : undefined,
          inclusions: inclusions ? inclusions.split(',').map(s => s.trim()).filter(Boolean) : undefined,
        },
      } as any;
    }
    return {
      start_date: toIsoFromLocal(legacyStart) || '',
      when: legacyWhen || '',
    } as any;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload: Partial<EventItem> = {
        title,
        websitePK,
        date: buildEventDate(),
        venue,
        description,
        address,
        link: link || undefined,
        thumbnail: thumbnail || undefined,
        imageUrl: imageUrl || undefined,
        locationPK: locationPK || undefined,
      };
      await onSubmit(payload);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const addNthWeekday = () => setByWeekdayNth(prev => [...prev, { weekday: 'MO', nth: [1] }]);
  const updateNthWeekday = (idx: number, value: RecurringByWeekdayNth) => setByWeekdayNth(prev => prev.map((x, i) => i === idx ? value : x));
  const removeNthWeekday = (idx: number) => setByWeekdayNth(prev => prev.filter((_, i) => i !== idx));

  const dateSummary = useMemo(() => {
    if (dateKind === 'single') {
      if (!singleStart) return '';
      const start = new Date(singleStart);
      const startDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(start);
      const startTime = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(start);
      if (singleEnd) {
        const end = new Date(singleEnd);
        const sameDay = start.toDateString() === end.toDateString();
        const endTime = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(end);
        return sameDay ? `${startDate} ${startTime} – ${endTime}` : `${startDate} ${startTime} to ${fmtDateWithYear(end.toISOString())} ${endTime}`;
      }
      return `${startDate} ${startTime}`;
    }
    if (dateKind === 'span') {
      if (!spanStartDate || !spanEndDate) return '';
      const range = `${fmtDate(spanStartDate)} to ${fmtDate(spanEndDate)}`;
      const times = spanStartTime || spanEndTime ? ` • Daily ${fmtAmpm(spanStartTime)}${spanStartTime && spanEndTime ? ' – ' : ''}${fmtAmpm(spanEndTime)}` : '';
      return `${range}${times}`;
    }
    if (dateKind === 'set') {
      const occs = occurrences.filter(o => o.date);
      if (!occs.length) return '';
      const first = occs.slice(0, 3).map(o => `${fmtDate(o.date)}${o.startTime ? ` ${fmtAmpm(o.startTime)}` : ''}${o.endTime ? ` – ${fmtAmpm(o.endTime)}` : ''}`).join('; ');
      const extra = occs.length > 3 ? ` (+${occs.length - 3} more)` : '';
      return `${first}${extra}`;
    }
    if (dateKind === 'recurring') {
      let core = '';
      if (freq === 'DAILY') core = 'Daily';
      if (freq === 'WEEKLY') core = byWeekdaySimple.length ? byWeekdaySimple.map(w => ({ SU: 'Sundays', MO: 'Mondays', TU: 'Tuesdays', WE: 'Wednesdays', TH: 'Thursdays', FR: 'Fridays', SA: 'Saturdays' } as any)[w]).join(', ') : 'Weekly';
      if (freq === 'MONTHLY') {
        const nth = byWeekdayNth.map(w => `${w.nth.map(ordinal).join(', ')} ${({ SU: 'Sun', MO: 'Mon', TU: 'Tue', WE: 'Wed', TH: 'Thu', FR: 'Fri', SA: 'Sat' } as any)[w.weekday]}`);
        core = nth.length ? `Monthly on ${nth.join(', ')}` : 'Monthly';
      }
      if (freq === 'YEARLY') core = 'Yearly';
      const time = ruleStartTime || ruleEndTime ? ` ${fmtAmpm(ruleStartTime)}${ruleStartTime && ruleEndTime ? ' – ' : ''}${fmtAmpm(ruleEndTime)}` : '';
      const untilStr = until ? ` through ${fmtDate(until)}` : '';
      return `${core}${time}${untilStr}`;
    }
    if (legacyWhen) return legacyWhen;
    if (legacyStart) return `${fmtDateWithYear(legacyStart)} ${new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(new Date(legacyStart))}`;
    return '';
  }, [dateKind, singleStart, singleEnd, spanStartDate, spanEndDate, spanStartTime, spanEndTime, occurrences, freq, byWeekdaySimple, byWeekdayNth, ruleStartTime, ruleEndTime, until, legacyWhen, legacyStart]);

  const collapsedSummaryText = dateSummary || 'No date specified';

  // New: parse date phrase from description and apply to controls
  const parseAndApplyDateFromDescription = (text: string) => {
    const today = new Date();
    const year = today.getFullYear();
    const head = (text || '').split(/\n/)[0]?.split(':')[0] || '';
    const line = head.trim();
    if (!line) return;

    // 1) Weekly with times: "Sundays from 11 a.m. to 4 p.m."
    const weeklyTimes = line.match(/^(sundays?|mondays?|tuesdays?|wednesdays?|thursdays?|fridays?|saturdays?)\s+(?:from\s+([^\s].*?)\s*(?:to|–|-)\s*([^\s].*?))\.?$/i);
    if (weeklyTimes) {
      const weekday = weekdayToCode(weeklyTimes[1]);
      const startT = parseTimeString(weeklyTimes[2]);
      const endT = parseTimeString(weeklyTimes[3]);
      if (weekday) {
        setDateKind('recurring');
        setFreq('WEEKLY');
        setByWeekdaySimple([weekday]);
        setByWeekdayNth([]);
        setRuleStartTime(startT || '');
        setRuleEndTime(endT || '');
        return;
      }
    }

    // 2) "Wednesday evenings through Oct. 29"
    const weekdayThrough = line.match(/^(monday|tuesday|wednesday|thursday|friday|saturday|sunday)s?\s+evenings?\s+through\s+([A-Za-z]+)\.?\s*(\d{1,2})\b/i);
    if (weekdayThrough) {
      const weekday = weekdayToCode(weekdayThrough[1]);
      const monthNum = monthToNumber(weekdayThrough[2]);
      const day = parseInt(weekdayThrough[3], 10);
      if (weekday && monthNum) {
        const untilStr = `${year}-${String(monthNum).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        setDateKind('recurring');
        setFreq('WEEKLY');
        setByWeekdaySimple([weekday]);
        setByWeekdayNth([]);
        setUntil(untilStr);
        setRuleStartTime('');
        setRuleEndTime('');
        return;
      }
    }

    // 3) "Daily through Oct. 31"
    const dailyThrough = line.match(/^daily\s+through\s+([A-Za-z]+)\.?\s*(\d{1,2})\b/i);
    if (dailyThrough) {
      const monthNum = monthToNumber(dailyThrough[1]);
      const day = parseInt(dailyThrough[2], 10);
      if (monthNum) {
        const untilStr = `${year}-${String(monthNum).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        setDateKind('recurring');
        setFreq('DAILY');
        setByWeekdaySimple([]);
        setByWeekdayNth([]);
        setUntil(untilStr);
        setRuleStartTime('');
        setRuleEndTime('');
        return;
      }
    }

    // 4) "Oct. 3 to 6" OR "Oct. 3 to Oct. 6"
    const spanShort = line.match(/^([A-Za-z]+)\.?\s*(\d{1,2})\s*(?:to|–|-)\s*(?:([A-Za-z]+)\.?\s*)?(\d{1,2})\b/i);
    if (spanShort) {
      const m1 = monthToNumber(spanShort[1]);
      const d1 = parseInt(spanShort[2], 10);
      const m2 = spanShort[3] ? monthToNumber(spanShort[3]) : m1;
      const d2 = parseInt(spanShort[4], 10);
      if (m1 && m2) {
        setDateKind('span');
        setSpanStartDate(`${year}-${String(m1).padStart(2, '0')}-${String(d1).padStart(2, '0')}`);
        setSpanEndDate(`${year}-${String(m2).padStart(2, '0')}-${String(d2).padStart(2, '0')}`);
        setSpanStartTime('');
        setSpanEndTime('');
        return;
      }
    }

    // 5) Single date like "Oct. 10" optionally with time "7:00 PM"
    const singleWithTime = line.match(/^([A-Za-z]+)\.?\s*(\d{1,2})(?:,?\s*(\d{4}))?(?:\s+at\s+([\w:\.\s]+))?\b/i);
    if (singleWithTime) {
      const m = monthToNumber(singleWithTime[1]);
      const d = parseInt(singleWithTime[2], 10);
      const y = singleWithTime[3] ? parseInt(singleWithTime[3], 10) : year;
      const t = parseTimeString(singleWithTime[4]);
      if (m) {
        if (t) {
          const dt = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}T${t}`;
          setDateKind('single');
          setSingleStart(dt);
          setSingleEnd('');
        } else {
          setDateKind('single');
          setSingleStart(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}T12:00`);
          setSingleEnd('');
        }
        return;
      }
    }
  };

  // Override description onChange to parse leading date phrase
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    setDescription(value);
    // Try parsing only if the first line begins with a letter (likely a phrase)
    if (/^[A-Za-z]/.test((value || '').trim())) {
      try { parseAndApplyDateFromDescription(value); } catch { /* tolerate parser errors */ }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Event' : 'New Event'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)} error={!!errors['title']} helperText={errors['title']} />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="website-label">Website</InputLabel>
              <Select labelId="website-label" label="Website" value={websitePK} onChange={(e) => setWebsitePK(e.target.value as string)} error={!!errors['websitePK']}>
                {websiteOptions.map(opt => (<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth multiline rows={4} label="Description" value={description} onChange={handleDescriptionChange} error={!!errors['description']} helperText={errors['description']} />
          </Grid>

          <Grid item xs={12}>
            <CollapsibleSection
              title="Date & Time"
              icon={<EventIcon sx={{ mr: 1 }} />}
              defaultExpanded={false}
              namespace="events-form"
              id="date-controls"
              collapsedSummary={collapsedSummaryText}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="date-kind-label">Date Type</InputLabel>
                    <Select labelId="date-kind-label" label="Date Type" value={dateKind} onChange={(e) => setDateKind(e.target.value as any)}>
                      <MenuItem value="single">Single</MenuItem>
                      <MenuItem value="span">Span</MenuItem>
                      <MenuItem value="set">Set</MenuItem>
                      <MenuItem value="recurring">Recurring</MenuItem>
                      <MenuItem value="legacy">Legacy</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Timezone (IANA)" value={timezone} onChange={(e) => setTimezone(e.target.value)} placeholder="America/Los_Angeles" />
                </Grid>

                {dateKind === 'single' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth type="datetime-local" label="Start" value={singleStart} onChange={(e) => setSingleStart(e.target.value)} error={!!errors['date.singleStart']} helperText={errors['date.singleStart']} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth type="datetime-local" label="End (optional)" value={singleEnd} onChange={(e) => setSingleEnd(e.target.value)} InputLabelProps={{ shrink: true }} />
                    </Grid>
                  </>
                )}

                {dateKind === 'span' && (
                  <>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth type="date" label="Start date" value={spanStartDate} onChange={(e) => setSpanStartDate(e.target.value)} error={!!errors['date.spanStartDate']} helperText={errors['date.spanStartDate']} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth type="date" label="End date" value={spanEndDate} onChange={(e) => setSpanEndDate(e.target.value)} error={!!errors['date.spanEndDate']} helperText={errors['date.spanEndDate']} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth type="time" label="Daily start time" value={spanStartTime} onChange={(e) => setSpanStartTime(e.target.value)} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth type="time" label="Daily end time" value={spanEndTime} onChange={(e) => setSpanEndTime(e.target.value)} InputLabelProps={{ shrink: true }} />
                    </Grid>
                  </>
                )}

                {dateKind === 'set' && (
                  <Grid item xs={12}>
                    <Stack spacing={1}>
                      {occurrences.map((o, i) => (
                        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <TextField type="date" label={`Date #${i + 1}`} value={o.date} onChange={(e) => setOccurrences(prev => prev.map((x, idx) => idx === i ? { ...x, date: e.target.value } : x))} InputLabelProps={{ shrink: true }} sx={{ minWidth: 200 }} />
                          <TextField type="time" label="Start" value={o.startTime || ''} onChange={(e) => setOccurrences(prev => prev.map((x, idx) => idx === i ? { ...x, startTime: e.target.value } : x))} InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
                          <TextField type="time" label="End" value={o.endTime || ''} onChange={(e) => setOccurrences(prev => prev.map((x, idx) => idx === i ? { ...x, endTime: e.target.value } : x))} InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
                          <IconButton aria-label="remove" color="error" onClick={() => setOccurrences(prev => prev.filter((_, idx) => idx !== i))} disabled={occurrences.length === 1}><DeleteIcon /></IconButton>
                        </Stack>
                      ))}
                      <Button startIcon={<AddIcon />} onClick={() => setOccurrences(prev => [...prev, { date: '', startTime: '', endTime: '' }])} sx={{ alignSelf: 'flex-start' }}>Add Date</Button>
                      {errors['date.set.0'] && <Typography color="error" variant="caption">{errors['date.set.0']}</Typography>}
                    </Stack>
                  </Grid>
                )}

                {dateKind === 'recurring' && (
                  <>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel id="freq-label">Frequency</InputLabel>
                        <Select labelId="freq-label" label="Frequency" value={freq} onChange={(e) => setFreq(e.target.value as any)}>
                          <MenuItem value="DAILY">Daily</MenuItem>
                          <MenuItem value="WEEKLY">Weekly</MenuItem>
                          <MenuItem value="MONTHLY">Monthly</MenuItem>
                          <MenuItem value="YEARLY">Yearly</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextField fullWidth type="date" label="Start (dtstart)" value={dtstart} onChange={(e) => setDtstart(e.target.value)} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextField fullWidth type="date" label="Until (optional)" value={until} onChange={(e) => setUntil(e.target.value)} InputLabelProps={{ shrink: true }} />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>Weekdays</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {weekdayLabels.map(w => (
                          <Chip key={w.code} label={w.label} color={byWeekdaySimple.includes(w.code) ? 'primary' : 'default'} onClick={() => setByWeekdaySimple(prev => prev.includes(w.code) ? prev.filter(x => x !== w.code) : [...prev, w.code])} />
                        ))}
                      </Stack>
                    </Grid>

                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">Nth Weekdays (Monthly)</Typography>
                        {byWeekdayNth.map((w, idx) => (
                          <Stack key={idx} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <Select size="small" value={w.weekday} onChange={(e) => updateNthWeekday(idx, { ...w, weekday: e.target.value as WeekdayCode })} sx={{ minWidth: 120 }}>
                              {weekdayLabels.map(opt => (<MenuItem key={opt.code} value={opt.code}>{opt.label}</MenuItem>))}
                            </Select>
                            <TextField size="small" label="Nth (comma-separated)" placeholder="e.g., 1,3" value={w.nth.join(',')} onChange={(e) => updateNthWeekday(idx, { ...w, nth: e.target.value.split(',').map(s => parseInt(s.trim())).filter(n => !Number.isNaN(n)) })} sx={{ minWidth: 200 }} />
                            <IconButton aria-label="remove" color="error" onClick={() => removeNthWeekday(idx)}><DeleteIcon /></IconButton>
                          </Stack>
                        ))}
                        <Button startIcon={<AddIcon />} onClick={addNthWeekday} sx={{ alignSelf: 'flex-start' }}>Add Nth Weekday</Button>
                      </Stack>
                    </Grid>

                    <Grid item xs={6}>
                      <TextField fullWidth type="time" label="Start time" value={ruleStartTime} onChange={(e) => setRuleStartTime(e.target.value)} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth type="time" label="End time" value={ruleEndTime} onChange={(e) => setRuleEndTime(e.target.value)} InputLabelProps={{ shrink: true }} />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Stack spacing={1}>
                        <TextField label="Exclusions (YYYY-MM-DD, comma-separated)" value={exclusions} onChange={(e) => setExclusions(e.target.value)} />
                        <TextField label="Inclusions (YYYY-MM-DD, comma-separated)" value={inclusions} onChange={(e) => setInclusions(e.target.value)} />
                      </Stack>
                    </Grid>
                  </>
                )}

                {dateKind === 'legacy' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth type="datetime-local" label="Start" value={legacyStart} onChange={(e) => setLegacyStart(e.target.value)} error={!!errors['date.legacy.start']} helperText={errors['date.legacy.start']} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="When (display text)" value={legacyWhen} onChange={(e) => setLegacyWhen(e.target.value)} placeholder="Fri, Oct 10 • 7:00 PM" />
                    </Grid>
                  </>
                )}
              </Grid>
            </CollapsibleSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Venue Name" value={venue?.name || ''} onChange={(e) => setVenue(prev => ({ ...(prev || {}), name: e.target.value }))} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField fullWidth label="Venue Rating" type="number" inputProps={{ step: '0.1', min: 0, max: 5 }} value={venue?.rating ?? ''} onChange={(e) => setVenue(prev => ({ ...(prev || {}), rating: e.target.value === '' ? undefined : Number(e.target.value) }))} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField fullWidth label="Venue Reviews" type="number" inputProps={{ step: '1', min: 0 }} value={venue?.reviews ?? ''} onChange={(e) => setVenue(prev => ({ ...(prev || {}), reviews: e.target.value === '' ? undefined : Number(e.target.value) }))} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Address Line 1" value={address?.[0] || ''} onChange={(e) => setAddress(prev => [e.target.value, ...(prev.slice(1) || [])])} error={!!errors['address.0']} helperText={errors['address.0']} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Address Line 2 (optional)" value={address?.[1] || ''} onChange={(e) => setAddress(prev => [prev?.[0] || '', e.target.value])} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://example.com/event/123" />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Thumbnail URL" value={thumbnail} onChange={(e) => setThumbnail(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Image URL" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>Location (optional)</Typography>
            <LocationSelector value={locationPK || ''} onChange={(value: string) => setLocationPK(value)} error={''} disabled={false} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventForm;

const toLocalDatetimeInput = (iso?: string): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  } catch {
    return '';
  }
};

const toIsoFromLocal = (local?: string): string | undefined => {
  if (!local) return undefined;
  try {
    const d = new Date(local);
    return d.toISOString();
  } catch {
    return undefined;
  }
};
